/**
 * «راهنمای برنامه» (v2.146.0) — همهٔ بخش‌ها به ترتیبِ منو، با جست‌وجو و سؤالاتِ پرتکرار.
 * کاربر: «برای تمامِ بخش‌ها به ترتیب برو تا همهٔ بخش‌ها حتی تنظیماتِ اصلی».
 */
import React, { useMemo, useState } from "react";
import { Card, Col, Empty, Input, Menu, Row, Segmented, Typography, Collapse } from "antd";
import { useSearchParams } from "react-router-dom";
import { GUIDES as ALL_GUIDES, GUIDE_GROUPS } from "../help/guides";
import { useAuth } from "../context/AuthContext";
import GuideView from "../help/GuideView";

const { Title, Text } = Typography;
const norm = (s) => String(s || "").replace(/ي/g, "ی").replace(/ك/g, "ک").replace(/‌/g, " ").toLowerCase();

export default function GuidePage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("guides");
  // راهنمای بخش‌های انحصاری (مثلِ اموال) فقط برای کسی که دسترسی دارد دیده می‌شود
  const { can } = useAuth();
  const GUIDES = useMemo(() => ALL_GUIDES.filter((g) => !g.perm || can(g.perm)), [can]);
  const current = GUIDES.find((g) => g.id === sp.get("g")) || GUIDES[0];
  const hit = (g) => !q.trim() || norm([g.title, g.summary, ...(g.steps || []), ...(g.faq || []).flat(),
    ...(g.fields || []).flat()].join(" ")).includes(norm(q));
  const visible = useMemo(() => GUIDES.filter(hit), [q, GUIDES]);   // eslint-disable-line
  const menuItems = GUIDE_GROUPS.map((grp) => ({
    key: grp.id, type: "group", label: grp.title,
    children: visible.filter((g) => g.group === grp.id).map((g) => ({ key: g.id, label: g.title })),
  })).filter((grp) => grp.children.length);
  const allFaq = useMemo(() => GUIDES.flatMap((g) => (g.faq || []).map(([qq, a]) => ({ g, q: qq, a })))
    .filter((x) => !q.trim() || norm(x.q + " " + x.a + " " + x.g.title).includes(norm(q))), [q, GUIDES]);

  return (
    <div>
      {/* عنوان‌های بلندِ منو کامل خوانده شوند (نه «ثبت برش (بیجک…») */}
      <style>{`.gx-guide-menu .ant-menu-item{white-space:normal;height:auto!important;line-height:1.7!important;padding-block:6px}`}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>راهنمای برنامه</Title>
        <Segmented value={mode} onChange={setMode}
          options={[{ value: "guides", label: "راهنمای بخش‌ها" }, { value: "faq", label: `سؤالاتِ پرتکرار (${allFaq.length.toLocaleString("fa-IR")})` }]} />
        <Input.Search allowClear placeholder="جست‌وجو در راهنما… (مثلاً «پرداخت»، «بیجک»، «بیمه»)" style={{ maxWidth: 380 }}
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {mode === "faq" ? (
        <Card size="small">
          {!allFaq.length ? <Empty /> : (
            <Collapse items={allFaq.map((x, i) => ({
              key: String(i), label: <span>{x.q} <Text type="secondary" style={{ fontSize: 12 }}>— {x.g.title}</Text></span>,
              children: <div>{x.a}<div><a onClick={() => { setMode("guides"); setSp({ g: x.g.id }); }}>راهنمای کاملِ «{x.g.title}» ›</a></div></div>,
            }))} />
          )}
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          <Col xs={24} md={7} lg={6}>
            <Card size="small" styles={{ body: { padding: 4 } }}>
              {!menuItems.length ? <Empty description="چیزی پیدا نشد" /> : (
                <Menu mode="inline" className="gx-guide-menu" selectedKeys={[current.id]} items={menuItems}
                  onClick={({ key }) => { setSp({ g: key }); window.scrollTo({ top: 0 }); }}
                  style={{ borderInlineEnd: 0, maxHeight: "75vh", overflowY: "auto" }} />
              )}
            </Card>
          </Col>
          <Col xs={24} md={17} lg={18}>
            <Card size="small"><GuideView guide={current} /></Card>
          </Col>
        </Row>
      )}
    </div>
  );
}
